import type { DocumentResource } from "@/lib/document-resource";
import { useDocumentResourceFactory } from "@/lib/use-open-document";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl, type IntlShape } from "react-intl";
import { useMe } from "@/auth/use-me";
import type { DocumentItem } from "@/components/documents/types";
import { CreateDocumentDocument, MyDocumentGroupsDocument, MyDocumentsDocument, type MyDocumentsQuery } from "@/graphql/generated/operations";
import { throwForValidationResult, userFacingGraphqlError } from "@/lib/graphql-error";
import { MAX_DOCUMENT_UPLOAD_BYTES, formatMegabytes } from "@/lib/upload-limits";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { partitionUploads } from "./duplicate-guards";

// The user's own documents (Save · Documents) as every place that works with them needs them: the Documents
// page and the Studio notebook, which brings documents in from there. One adapter, so the two list, name and
// upload them the same way.

// The documents are loaded in full: folder counts/contents and the "X of Y" tally must reflect every item,
// not just the first server batch. We walk the pages in PAGE_FETCH-sized chunks (the cursor merge in
// graphql/client.ts accumulates them).
export const PAGE_FETCH = 100;

export function readFileAsBase64(file: File): Promise<{ mimeType: string; content: string; fileName: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const comma = dataUrl.indexOf(",");
      resolve({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        content: comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function documentFileType(fileName: string | null | undefined, category: string | null | undefined, isCv: boolean): string {
  const ext = fileName?.match(/\.([a-z0-9]{2,8})$/i)?.[1];
  if (ext) return ext.toUpperCase();
  if (category) return category.toUpperCase();
  if (isCv) return "PDF";
  return "DOC";
}

type DocumentNode = MyDocumentsQuery["documents"]["nodes"][number];
type PersonalDocument = DocumentNode & { document: DocumentResource };

/** A stored document as the extraction pickers take it; they decide by the file it holds. */
export function toExtractionDoc(d: DocumentNode) {
  return {
    id: d.id,
    name: d.name ?? "",
    fileName: d.fileName ?? undefined,
    groupId: d.group?.id ?? null,
    createdAt: d.createdAt ?? null,
  };
}

export function toDocumentItem(d: PersonalDocument, intl: IntlShape): DocumentItem {
  // Legacy: older generator runs stored CVs as PDF documents tagged
  // "Lebenslauf" - keep labelling those, they still open as PDF.
  const isCv = (d.tags as string[] | null)?.includes("Lebenslauf") ?? false;
  const fileName = d.fileName as string | null;
  const name = (d.name as string | null) ?? fileName ?? "-";
  const fileType = documentFileType(fileName ?? name, d.category as string | null, isCv);
  return {
    id: d.id,
    document: d.document,
    canMove: true,
    canDownload: true,
    kind: isCv ? "CV" : fileType,
    name,
    when: d.createdAt as string | null,
    source: isCv
      ? intl.formatMessage({ id: "service.page.sourceCvGenerator", defaultMessage: "CV generator" })
      : intl.formatMessage({ id: "service.page.sourceUpload", defaultMessage: "File upload" }),
    fileType,
    groupId: (d.group?.id as string | null) ?? null,
    preview: d.preview?.content
      ? `data:${d.preview.mimeType ?? "image/png"};base64,${d.preview.content}`
      : null,
  };
}

/** The user's documents, every page drained, and their folders. */
export function useMyDocuments() {
  const intl = useIntl();
  const { data, loading, error, refetch, fetchMore } = useQuery(MyDocumentsDocument, { variables: { input: { first: PAGE_FETCH } } });
  const docNodes = data?.documents?.nodes;
  const createResource = useDocumentResourceFactory();
  const docs = useMemo(() => (docNodes ?? []).map(d => ({ ...d, document: createResource(d.id, d.fileName ?? d.name ?? "document") })), [docNodes, createResource]);
  // Each appended page advances pageInfo, which re-runs the effect until the connection reports no further
  // pages; a failed page stops the walk until the user retries.
  const [paginationError, setPaginationError] = useState<string | null>(null);
  const pageInfo = data?.documents?.pageInfo;
  useEffect(() => {
    if (!paginationError && pageInfo?.hasNextPage && pageInfo.endCursor) {
      void fetchMore({
        variables: { input: { first: PAGE_FETCH, after: pageInfo.endCursor } },
      }).catch(failure => setPaginationError(userErrorMessage(intl, failure)));
    }
  }, [pageInfo, fetchMore, intl, paginationError]);
  const { data: groupsData, refetch: groupsRefetch } = useQuery(MyDocumentGroupsDocument, { variables: { input: {} } });
  // DocumentGroupOutput is a single-member union - unwrap via the any seam.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupsOut: any = groupsData?.documentGroups;
  const groups = (groupsOut?.documentGroups ?? []) as readonly { id: string; name: string }[];
  return {
    docs,
    groups,
    loading,
    draining: Boolean(pageInfo?.hasNextPage),
    error,
    paginationError,
    retryPagination: () => setPaginationError(null),
    refetch,
    groupsRefetch,
  };
}

/**
 * Uploads files as the user's documents, the Documents page's rules: at most MAX_DOCUMENT_UPLOAD_BYTES per
 * file, and no second document under a name already taken (genesis has no unique constraint on the name, so
 * nothing downstream catches what this misses). Returns what it created; `onError` hears what it refused.
 */
export function useUploadMyDocuments({ storedNames, onError }: {
  storedNames: readonly { id: string; name: string }[];
  onError: (message: string | null) => void;
}) {
  const intl = useIntl();
  const me = useMe();
  const [createDocument] = useMutation(CreateDocumentDocument);
  const [busy, setBusy] = useState(false);
  const upload = async (files: FileList | File[] | null, groupId: string | null = null): Promise<{ id: string; name: string }[]> => {
    if (!files || busy) return [];
    if (!me.id) {
      onError(intl.formatMessage({ id: "service.page.errNotSignedInUpload", defaultMessage: "Not signed in - upload not possible." }));
      return [];
    }
    // `onError` holds one message, so everything held back shares it; a later call would hide the earlier one.
    const refused: string[] = [];
    const tooBig = Array.from(files).filter((f) => f.size > MAX_DOCUMENT_UPLOAD_BYTES);
    if (tooBig.length) {
      refused.push(
        intl.formatMessage(
          { id: "service.page.errUploadTooLarge", defaultMessage: "Too large (max. {max} per file): {names}." },
          { max: formatMegabytes(MAX_DOCUMENT_UPLOAD_BYTES), names: tooBig.map((f) => f.name).join(", ") },
        ),
      );
    }
    const sized = Array.from(files).filter((f) => f.size <= MAX_DOCUMENT_UPLOAD_BYTES);
    const { uploadable, taken } = partitionUploads(sized, storedNames);
    if (taken.length) {
      refused.push(
        intl.formatMessage(
          { id: "service.page.errUploadNameTaken", defaultMessage: "Already in Documents, not uploaded again: {names}." },
          { names: taken.map((f) => f.name).join(", ") },
        ),
      );
    }
    if (refused.length) onError(refused.join(" "));
    if (!uploadable.length) return [];
    if (!refused.length) onError(null);
    setBusy(true);
    const created: { id: string; name: string }[] = [];
    try {
      for (const f of uploadable) {
        const read = await readFileAsBase64(f);
        const res = await createDocument({
          variables: {
            input: {
              data: {
                name: read.fileName,
                person: { id: me.id },
                tags: [],
                // Drop target: upload straight into a folder (or loose = null).
                group: groupId ? { id: groupId } : null,
                // fileName is a top-level Document field; CreateFileDataInput
                // (content) only accepts mimeType + content.
                fileName: read.fileName,
                content: { mimeType: read.mimeType, content: read.content },
              },
              dryRun: false,
            },
          },
        });
        const out = res.data?.createDocument;
        if (out?.__typename !== "CreateDocumentResult") {
          throwForValidationResult(
            out,
            intl.formatMessage({ id: "service.page.errUploadDuplicate", defaultMessage: "Upload failed. A document with this name probably already exists." }),
          );
        }
        created.push({ id: out.result.id, name: read.fileName });
      }
    } catch (e) {
      logApplicationFailure("save.service-upload");
      onError([
        ...refused,
        userFacingGraphqlError(
          intl,
          e,
          intl.formatMessage({ id: "service.page.errUploadDuplicate", defaultMessage: "Upload failed. A document with this name probably already exists." }),
        ),
      ].join(" "));
    } finally {
      setBusy(false);
    }
    return created;
  };
  return { upload, busy };
}
